import classNames from "classnames";
import { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  isValidEntityType,
  QueryPanelLogic,
  Query,
  Position,
  RANGE_FILTERS,
  Filters,
} from "types";
import {
  getFilterIcon,
  getOperatorTitle,
  isSignOperator,
} from "helpers/utility/queryFilterHelpers";
import {
  getMislabelsThresholdRange,
  isSavedViewsEnabled,
} from "redux/SingleDataset/selectors";
import { setSelectedQueryModal } from "redux/SingleDataset/actions";
import { getFalseRangeDisplayValue } from "helpers/utility/utilities";

import SaveViewMenu from "views/pages/DataPage/CardsView/DataPageFilters/SaveViewMenu";
import { ENTITY_TYPE_PARAMETER } from "helpers/constants/miscellaneous";
import AddFilter from "views/components/QueryPanel/AddFilter";
import QueryTab from "views/components/QueryTab";
import useQueryParam from "hooks/useQueryParam";

import { FaRegStar } from "react-icons/fa";
import useVqlParams from "hooks/useVqlParams";
import styles from "./style.module.scss";
import { isCustomMetadata } from "helpers/utility/customMetadataHelpers";
import { formatDateWithHours } from "helpers/utility/formatters";
interface QueryPanelRowProps {
  type?: QueryPanelLogic;
  queries: Query[];
  onReset: () => void;
  onRemove: (q: Query) => void;
  onClick: (q: Query, p: Position) => void;
}

const QueryPanelRow = ({
  queries,
  type = QueryPanelLogic.AND,
  onReset,
  onClick,
  onRemove,
}: QueryPanelRowProps) => {
  const entityParam = useQueryParam(ENTITY_TYPE_PARAMETER);
  const entityTypeFromQueryParam = isValidEntityType(entityParam)
    ? entityParam
    : null;

  const mislabelsRange = useSelector((state) =>
    getMislabelsThresholdRange(state, entityTypeFromQueryParam)
  );

  const rowRef = useRef<HTMLDivElement>(null);

  const logicMap: Record<QueryPanelLogic, string> = {
    [QueryPanelLogic.EXCLUDE]: "Exclude",
    [QueryPanelLogic.OR]: "OR",
    [QueryPanelLogic.AND]: "",
  };

  const logicText = logicMap[type] || "";

  const getTabLabels = (query: Query): string[] => {
    const { type, values } = query;

    const showFalseValue = [
      Filters.ISSUES,
      Filters.OUTLIERS,
      Filters.LABEL_OUTLIER,
    ].includes(type as Filters);

    if (showFalseValue && values.length === 2) {
      return values.map((v) =>
        getFalseRangeDisplayValue(parseFloat(v)).toString()
      );
    }

    if (isCustomMetadata(query.type)) {
      const { field_type } = query.type;
      if (field_type === "datetime") {
        if (values.length === 1) {
          return [formatDateWithHours(values[0])];
        } else if (values.length === 2) {
          return [
            formatDateWithHours(values[0]),
            formatDateWithHours(values[1]),
          ];
        }
      }
      if (field_type === "link") {
        return values.map((v) => v.split("/").pop() || v);
      }
    }

    return values;
  };

  const checkIsClickAllowed = (query: Query): boolean => {
    //if thresholds donot exist for mislabels, do not open modal
    if (query.type === Filters.MISLABELS && !mislabelsRange) {
      return false;
    }

    //default
    return true;
  };

  return (
    <div className={styles.row} ref={rowRef} data-testid="query-panel-row">
      {logicText && (
        <div className={styles.logic} data-testid="query-panel-row-logic-text">
          {logicText}
        </div>
      )}

      {queries.map((query) => {
        const isRange =
          !isCustomMetadata(query.type) && RANGE_FILTERS.includes(query.type);
        const isClickAllowed = checkIsClickAllowed(query);
        const labels = getTabLabels(query);
        const icon = getFilterIcon(query.type);
        const operator = getOperatorTitle(query.operator, query.type);
        const noColon = isSignOperator(operator);

        return (
          <QueryTab
            icon={icon}
            key={query.id}
            id={query.id}
            labels={labels}
            isRange={isRange}
            noColon={noColon}
            type={query.type}
            operator={operator}
            subType={query.subType}
            onClick={
              isClickAllowed
                ? (position: Position) => onClick(query, position)
                : undefined
            }
            onRemove={() => onRemove(query)}
          />
        );
      })}
      <AddFilter rowRef={rowRef} />
      {queries.length > 0 && (
        <>
          <div className={styles.separator}></div>

          <div
            data-testid="query-panel-row-reset-btn"
            className={styles.resetBtn}
            onClick={onReset}
          >
            Reset
          </div>
        </>
      )}
    </div>
  );
};

const QueryPanel = () => {
  const dispatch = useDispatch();

  const savedViewsEnabled = useSelector((state) => isSavedViewsEnabled(state));
  const { vqlQueries, removeVqlQuery, setVqlQueries, vqlToQuery } =
    useVqlParams();
  const [saveViewMenuOpen, setSaveViewMenuOpen] = useState(false);

  const anyFilterApplied = vqlQueries.length > 0;

  const rowsClassnames = classNames(styles.rows, {
    [styles.fullWidth]: !(savedViewsEnabled && anyFilterApplied),
  });

  const actionButtonClassnames = classNames(styles.actionButtons, {
    [styles.hide]: !(savedViewsEnabled && anyFilterApplied),
  });

  const handleRemoval = (query: Query) => {
    removeVqlQuery(query.id);
  };

  const handleRemoveAll = () => {
    setVqlQueries([]);
  };

  const handleSelection = (query: Query, position: Position) => {
    dispatch(
      setSelectedQueryModal({
        id: query.id,
        type: query.type,
        position: position,
      })
    );
  };

  const handleSaveViewClick = () => {
    setSaveViewMenuOpen(!saveViewMenuOpen);
  };

  const queries: Query[] = vqlQueries
    .map(vqlToQuery)
    .filter((query): query is Query => query !== null);

  const renderRows = () => {
    return (
      <div className={rowsClassnames}>
        <QueryPanelRow
          key="andRow"
          queries={queries}
          onRemove={handleRemoval}
          onClick={handleSelection}
          onReset={handleRemoveAll}
        />
      </div>
    );
  };

  return (
    <div className={styles.queryPanel} data-testid="query-panel">
      {renderRows()}

      <div className={actionButtonClassnames}>
        {savedViewsEnabled && anyFilterApplied && (
          <div
            data-testid="query-panel-save-view-btn"
            className={styles.saveViewBtn}
            onClick={handleSaveViewClick}
          >
            <div className={styles.btnIcon}>
              <FaRegStar />
            </div>
            Save View
            {saveViewMenuOpen && (
              <div
                className={styles.saveViewMenuContainer}
                onClick={(e) => e.stopPropagation()}
              >
                <SaveViewMenu onCloseMenu={() => setSaveViewMenuOpen(false)} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default QueryPanel;
